import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu, X, ArrowRight } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = [
    { name: 'Home', path: '/' },
    {
      name: 'Services',
      path: '#services',
      dropdown: [
        { name: 'AI Development', path: '/ai-development' },
        { name: 'AI Consulting', path: '/ai-consulting' },
        { name: 'Software Development', path: '/software-development' }
      ]
    },
    {
      name: 'Products',
      path: '#products',
      dropdown: [
        { name: 'InsightAI', path: '/custom-rag-demo' }
      ]
    },
    { name: 'Resources', path: '/resources' },
    { name: 'About', path: '/about' },
    {
      name: 'Initiative',
      path: '#initiative',
      dropdown: [
        { name: 'ClassIQ', path: 'https://classiq.thethinkverse.ai', external: true }
      ]
    }
  ];

  const toggleDropdown = (itemName) => {
    setOpenDropdown(openDropdown === itemName ? null : itemName);
  };

  const handleAnchorClick = (e, path) => {
    if (path.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(path);
      if (element) {
        const navbarHeight = 80;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        setMobileMenuOpen(false);
        setOpenDropdown(null);
      }
    }
  };

  const handleGetStartedClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      handleAnchorClick(e, '#contact');
    } else {
      navigate('/', { state: { scrollTo: 'contact' } });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${isScrolled
          ? 'bg-white/80 backdrop-blur-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-b border-white/20 py-3'
          : 'bg-transparent py-5'
        }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo Section */}
          <Link to="/" className="flex items-center gap-3 group relative z-10">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-100 to-purple-100 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_intelligent-home-1/artifacts/w793r8mv_ThinkVerse.AI%20%282%29.png"
                alt="ThinkVerse.AI"
                className="h-10 w-auto relative z-10 transition-transform duration-700 group-hover:scale-105 group-hover:rotate-[2deg]"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-slate-900 font-bold text-xl tracking-tight leading-none group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-500">
                ThinkVerse.AI
              </span>
              <span className="text-slate-500 text-[10px] font-medium tracking-wide uppercase mt-0.5 group-hover:text-slate-700 transition-colors duration-500">
                Transform Problems into Intelligent Solutions
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-1.5" ref={dropdownRef}>
            {menuItems.map((item) => (
              <div key={item.name} className="relative">
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 border border-transparent ${openDropdown === item.name
                          ? 'bg-blue-50/80 text-blue-600 border-blue-100 shadow-inner'
                          : 'text-slate-600 hover:text-blue-600 hover:bg-white/60 hover:shadow-sm hover:border-slate-100'
                        }`}
                    >
                      {item.name}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-500 ${openDropdown === item.name ? 'rotate-180 text-blue-600' : 'text-slate-400'
                          }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={`absolute top-full left-0 mt-3 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-[0_20px_40px_-12px_rgba(0,0,0,0.1)] border border-white/40 ring-1 ring-slate-900/5 overflow-hidden transition-all duration-300 origin-top-left ${openDropdown === item.name
                          ? 'opacity-100 scale-100 translate-y-0'
                          : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'
                        }`}
                    >
                      <div className="p-2 space-y-1">
                        {item.dropdown.map((subItem) => (
                          subItem.external ? (
                            <a
                              key={subItem.name}
                              href={subItem.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between px-4 py-3 rounded-xl text-sm text-slate-600 hover:text-blue-700 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-purple-50/80 transition-all duration-300 group"
                              onClick={() => setOpenDropdown(null)}
                            >
                              <span className="font-medium">{subItem.name}</span>
                              <ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-blue-500" />
                            </a>
                          ) : (
                            <Link
                              key={subItem.name}
                              to={subItem.path}
                              className="flex items-center justify-between px-4 py-3 rounded-xl text-sm text-slate-600 hover:text-blue-700 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-purple-50/80 transition-all duration-300 group"
                              onClick={() => setOpenDropdown(null)}
                            >
                              <span className="font-medium">{subItem.name}</span>
                              <ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-blue-500" />
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-white/60 hover:shadow-sm hover:border-slate-100 border border-transparent transition-all duration-300"
                    >
                      {item.name}
                    </a>
                  ) : item.path.startsWith('/') ? (
                    <Link
                      to={item.path}
                      className="block px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-white/60 hover:shadow-sm hover:border-slate-100 border border-transparent transition-all duration-300"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.path}
                      onClick={(e) => handleAnchorClick(e, item.path)}
                      className="block px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-white/60 hover:shadow-sm hover:border-slate-100 border border-transparent transition-all duration-300"
                    >
                      {item.name}
                    </a>
                  )
                )}
              </div>
            ))}

            {/* CTA Button */}
            <div className="ml-4 pl-4 border-l border-slate-200/60">
              <button
                onClick={handleGetStartedClick}
                className="group relative inline-flex items-center gap-2 px-6 py-2.5 bg-slate-900 hover:bg-blue-600 text-white text-sm font-semibold rounded-full transition-all duration-500 shadow-lg shadow-slate-900/20 hover:shadow-blue-600/30 hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="relative z-10">Get Started</span>
                <ArrowRight size={16} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all duration-300 active:scale-95"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden fixed inset-x-0 top-[72px] bg-white/95 backdrop-blur-2xl border-b border-slate-100 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
            }`}
        >
          <div className="px-6 py-8 space-y-4 max-h-[calc(100vh-80px)] overflow-y-auto">
            {menuItems.map((item) => (
              <div key={item.name} className="space-y-2">
                {item.dropdown ? (
                  <>
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className="flex items-center justify-between w-full text-left text-slate-800 font-semibold py-3 px-2 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      {item.name}
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-300 ${openDropdown === item.name ? 'rotate-180 text-blue-600' : 'text-slate-400'
                          }`}
                      />
                    </button>
                    <div
                      className={`pl-4 space-y-2 border-l-2 border-slate-100 ml-3 transition-all duration-300 ${openDropdown === item.name ? 'block' : 'hidden'
                        }`}
                    >
                      {item.dropdown.map((subItem) => (
                        subItem.external ? (
                          <a
                            key={subItem.name}
                            href={subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block py-2.5 px-3 rounded-lg text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="block py-2.5 px-3 rounded-lg text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {subItem.name}
                          </Link>
                        )
                      ))}
                    </div>
                  </>
                ) : (
                  item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-3 px-2 rounded-xl text-slate-800 font-semibold hover:bg-slate-50 hover:text-blue-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  ) : item.path.startsWith('/') ? (
                    <Link
                      to={item.path}
                      className="block py-3 px-2 rounded-xl text-slate-800 font-semibold hover:bg-slate-50 hover:text-blue-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.path}
                      onClick={(e) => handleAnchorClick(e, item.path)}
                      className="block py-3 px-2 rounded-xl text-slate-800 font-semibold hover:bg-slate-50 hover:text-blue-600 transition-colors"
                    >
                      {item.name}
                    </a>
                  )
                )}
              </div>
            ))}

            <div className="pt-6 mt-6 border-t border-slate-100">
              <button
                onClick={(e) => {
                  handleGetStartedClick(e);
                  setMobileMenuOpen(false);
                }}
                className="w-full py-4 bg-slate-900 text-white font-bold rounded-2xl shadow-lg shadow-slate-900/20 hover:bg-blue-600 hover:shadow-blue-600/30 transition-all duration-300"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
